// src/pages/ManufacturerPage.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import Select from "react-select";
import DataTable from "react-data-table-component";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL } from "../../config";

const ManufacturerPage = () => {
  const [showForm, setShowForm] = useState(false);

  // ---------- FORM STATE ----------
  const [form, setForm] = useState({
    name: "",
    company_name: "",
    contact_number: "",
    email: "",
    website: "",
    address_1: "",
    address_2: "",
    city: "",
    state: "",
    zipcode: "",
    country: "",
    phone_number: "",
    supplier_ids: [],
  });

  const [countries, setCountries] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [filterText, setFilterText] = useState("");

  // ---------- EDIT MODAL ----------
  const [showEdit, setShowEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    company_name: "",
    contact_number: "",
    email: "",
    website: "",
    address_1: "",
    address_2: "",
    city: "",
    state: "",
    zipcode: "",
    country: "",
    phone_number: "",
    supplier_ids: [],
  });

  /* ===========================================
   * LOADERS
   * ===========================================
   */

  const loadCountries = async () => {
    try {
      const res = await axios.get(API_BASE_URL + "/countries");
      setCountries(res.data?.data || res.data || []);
    } catch {
      toast.error("Failed to load countries");
    }
  };

  const loadSuppliers = async () => {
    try {
      const res = await axios.get(API_BASE_URL + "/get_suppliers");
      setSuppliers(res.data?.data || res.data || []);
    } catch {
      toast.error("Failed to load suppliers");
    }
  };

  const loadManufacturers = async () => {
    try {
      const res = await axios.get(API_BASE_URL + "/manufacture_list");
      setManufacturers(res.data?.data || res.data || []);
    } catch {
      toast.error("Failed to load manufacturers");
    }
  };

  useEffect(() => {
    loadCountries();
    loadSuppliers();
    loadManufacturers();
  }, []);

  /* ===========================================
   * ADD MANUFACTURER
   * ===========================================
   */

  const handleInput = (e) => {
    const { name, value } = e.target;

    if ((name === "contact_number" || name === "phone_number") && value.length > 10) {
      return;
    }

    setForm({ ...form, [name]: value });
  };

  const handleSupplierChange = (selected) => {
    setForm({
      ...form,
      supplier_ids: selected ? selected.map((x) => x.value) : [],
    });
  };

  const handleCountryChange = (selected) => {
    setForm({ ...form, country: selected ? selected.value : "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await axios.post(API_BASE_URL + "/add_manufacture", form);

      toast.success("Manufacturer added successfully!");

      setShowForm(false);

      setForm({
        name: "",
        company_name: "",
        contact_number: "",
        email: "",
        website: "",
        address_1: "",
        address_2: "",
        city: "",
        state: "",
        zipcode: "",
        country: "",
        phone_number: "",
        supplier_ids: [],
      });

      loadManufacturers();

    } catch (err) {
      if (err.response?.status === 422 && err.response.data?.errors) {
        Object.values(err.response.data.errors).forEach((msg) =>
          toast.error(msg[0], { position: "bottom-right" })
        );
      } else {
        toast.error("Error adding manufacturer");
      }
    }
  };

  /* ===========================================
   * EDIT MANUFACTURER
   * ===========================================
   */

  const openEditModal = async (row) => {
const id = row.manufacturer_id || row.id;

    try {
      const res = await axios.get(API_BASE_URL + `/manufacture_show/${id}`);
      const d = res.data.data || res.data;

      setEditId(id);
      setEditForm({
        name: d.manufacturer_name,
        company_name: d.company_name,
        contact_number: d.contact_number,
        email: d.manufacturer_email,
        website: d.website_url,
        address_1: d.address_1,
        address_2: d.address_2,
        city: d.city,
        state: d.state,
        zipcode: d.zipcode,
        country: d.country,
        phone_number: d.phone_number,
        supplier_ids: d.supplier_ids || [],
      });

      setShowEdit(true);
    } catch {
      toast.error("Failed to load manufacturer", { position: "bottom-right" });
    }
  };

  const handleEditInput = (e) => {
    const { name, value } = e.target;

    if ((name === "contact_number" || name === "phone_number") && value.length > 10) {
      return;
    }

    setEditForm({ ...editForm, [name]: value });
  };

  const handleEditSupplierChange = (selected) => {
    setEditForm({
      ...editForm,
      supplier_ids: selected ? selected.map((x) => x.value) : [],
    });
  };

  const handleEditCountryChange = (selected) => {
    setEditForm({
      ...editForm,
      country: selected ? selected.value : "",
    });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    try {
      await axios.put(API_BASE_URL + `/update_manufacture/${editId}`, editForm);

      toast.success("Manufacturer updated!");

      setShowEdit(false);
      loadManufacturers();
    } catch (err) {
      if (err.response?.status === 422 && err.response.data?.errors) {
        Object.values(err.response.data.errors).forEach((msg) =>
          toast.error(msg[0], { position: "bottom-right" })
        );
      } else {
        toast.error("Error updating manufacturer");
      }
    }
  };

  /* ===========================================
   * DATATABLE
   * ===========================================
   */

  const columns = [
    {
      name: "Action",
      width: "120px",
      cell: (row) => (
         <button
        className="border-0 bg-transparent me-2"
        title="Edit"
        onClick={() => openEditModal(row)}
        style={{ cursor: "pointer" }}
      >
        <i className="fa fa-pencil text-warning" style={{ fontSize: "18px" }}></i>
          </button>
      ),
    },
    {
      name: "Manufacturer Name",
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: "Supplier Name",
      selector: (row) => row.supplier_names,
      sortable: true,
    },
    {
      name: "Company",
      selector: (row) => row.company_name,
      sortable: true,
    },
    {
      name: "Email",
      selector: (row) => row.email,
      sortable: true,
    },
    {
      name: "Website",
      selector: (row) => row.website,
      sortable: true,
    },
    {
      name: "City",
      selector: (row) => row.city,
      sortable: true,
    },
    {
      name: "Country",
      selector: (row) => row.country_name,
      sortable: true,
    },
  ];

  const filteredItems = manufacturers.filter((item) => {
  const t = filterText.toLowerCase();

  return (
    (item.manufacturer_name || "").toLowerCase().includes(t) ||
    (item.company_name || "").toLowerCase().includes(t) ||
    (item.supplier_names || "").toLowerCase().includes(t) ||
    (item.city || "").toLowerCase().includes(t) ||
    (item.country_name || "").toLowerCase().includes(t)
  );
});


  /* ===========================================
   * SELECT OPTIONS
   * ===========================================
   */

  const countryOptions = countries.map((c) => ({
    label: c.country_name,
    value: c.country_id,
  }));

  const supplierOptions = suppliers.map((s) => ({
    label: s.supplier_name,
    value: s.supplier_id,
  }));

  /* ===========================================
   * RENDER
   * ===========================================
   */

  return (
    <div className="container mt-4">
     <ToastContainer position="bottom-right" />

      {/* ADD FORM TOGGLE HEADER */}
      <div className="card mb-3">
        <div className="card-header d-flex justify-content-between">
          <h5 className="mb-0">Manufacturers</h5>

          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={() => setShowForm(!showForm)}
          >
            <i className="ion ion-md-add"></i> Add New Manufacturer
          </button>
        </div>
      </div>

      {/* ADD FORM */}
      {showForm && (
        <div className="card mb-4">
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="row">
                {/* LEFT */}
                <div className="col-md-6">
                  <div className="mb-3">
                    <label>Manufacturer Name </label>
                    <input
                      className="form-control required-input"
                      name="name"
                      value={form.name}
                      onChange={handleInput}
                    />
                  </div>

                  <div className="mb-3">
                    <label>Company Name </label>
                    <input
                      className="form-control required-input"
                      name="company_name"
                      value={form.company_name}
                      onChange={handleInput}
                    />
                  </div>

                  <div className="mb-3">
                    <label>Contact Number  </label>
                    <input
                      className="form-control required-input"
                      name="contact_number"
                      value={form.contact_number}
                      onChange={handleInput}
                    />
                  </div>

                  <div className="mb-3">
                    <label>Email</label>
                    <input
                      className="form-control"
                      name="email"
                      value={form.email}
                      onChange={handleInput}
                    />
                  </div>

                  <div className="mb-3">
                    <label>Website</label>
                    <input
                      className="form-control"
                      name="website"
                      value={form.website}
                      onChange={handleInput}
                    />
                  </div>
                </div>

                {/* RIGHT */}
                <div className="col-md-6">
                  <div className="mb-3">
                    <label>Address 1</label>
                    <input
                      className="form-control mb-2 required-input"
                      name="address_1"
                      value={form.address_1}
                      onChange={handleInput}
                    />

                    <label>Address 2</label>
                    <input
                      className="form-control mb-2"
                      name="address_2"
                      value={form.address_2}
                      onChange={handleInput}
                    />

                    <div className="row mt-2">
                      <div className="col-md-4">
                        
                     
                        <input
                          className="form-control required-input"
                          placeholder="City "
                          name="city"
                          value={form.city}
                          onChange={handleInput}
                        />
                      </div>
                      <div className="col-md-4">
                        <input
                          className="form-control"
                          placeholder="State"
                          name="state"
                          value={form.state}
                          onChange={handleInput}
                        />
                      </div>
                      <div className="col-md-4">
                        <input
                          className="form-control"
                          placeholder="Zipcode"
                          name="zipcode"
                          value={form.zipcode}
                          onChange={handleInput}
                        />
                      </div>
                    </div>

                    <Select
                      className="mt-3"
                      options={countryOptions}
                      value={countryOptions.find((c) => c.value === form.country)}
                      onChange={handleCountryChange}
                      placeholder="Select country"
                    />
                  </div>
                </div>
              </div>

              {/* SECOND ROW */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <label>Phone Number   </label>
                  <input
                    className="form-control required-input"
                    name="phone_number"
                    value={form.phone_number}
                    onChange={handleInput}
                  />
                </div>

                <div className="col-md-6">
                  <label>Suppliers    <span style={{ color: "red" }}>*</span></label>
                  <Select classNames={{
    control: () => "required-select required-tooltip"
  }}
                    isMulti
                    options={supplierOptions}
                    value={supplierOptions.filter((s) =>
                      form.supplier_ids.includes(s.value)
                    )}
                    onChange={handleSupplierChange}
                  />
                </div>
              </div>

              <button type="submit" className="btn btn-primary">
                Save Manufacturer
              </button>
            </form>
          </div>
        </div>
      )}

      {/* TABLE */}
      <div className="card">
        <div className="card-header d-flex justify-content-between">
          <h6 className="mb-0">Manufacturer List</h6>
          <input
            type="text"
            className="form-control form-control-sm"
            style={{ width: "200px" }}
            placeholder="Search..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>

        <div className="card-body">
          <DataTable
            columns={columns}
            data={filteredItems}
            pagination
            highlightOnHover
            striped
          />
        </div>
      </div>

      {/* EDIT MODAL */}
     {showEdit && (
  <div
    className="modal fade show"
    style={{ display: "block", background: "rgba(0,0,0,.5)" }}
  >
    <div className="modal-dialog modal-lg">
      <div className="modal-content">

        <form onSubmit={handleUpdate}>

          {/* HEADER */}
          <div className="modal-header">
            <h5 className="modal-title">Edit Manufacturer</h5>
            <button
    type="button"
    onClick={() => setShowEdit(false)}
    style={{
      background: "transparent",
      border: "none",
      fontSize: "24px",
      fontWeight: "bold",
      cursor: "pointer",
      lineHeight: "1",
    }}
  >
    ×
  </button>
          </div>

          {/* BODY */}
          <div className="modal-body">

            <div className="row">

              {/* LEFT SIDE */}
              <div className="col-md-6">

                <div className="mb-3">
                  <label>Manufacturer Name</label>
                  <input
                    className="form-control required-input"
                    name="name"
                    value={editForm.name}
                    onChange={handleEditInput}
                  />
                </div>

                <div className="mb-3">
                  <label>Company Name</label>
                  <input
                    className="form-control required-input"
                    name="company_name"
                    value={editForm.company_name}
                    onChange={handleEditInput}
                  />
                </div>

                <div className="mb-3">
                  <label>Contact Number</label>
                  <input
                    className="form-control required-input"
                    name="contact_number"
                    value={editForm.contact_number}
                    onChange={handleEditInput}
                  />
                </div>

                <div className="mb-3">
                  <label>Email</label>
                  <input
                    className="form-control"
                    name="email"
                    value={editForm.email}
                    onChange={handleEditInput}
                  />
                </div>

                <div className="mb-3">
                  <label>Website</label>
                  <input
                    className="form-control"
                    name="website"
                    value={editForm.website}
                    onChange={handleEditInput}
                  />
                </div>

              </div>

              {/* RIGHT SIDE */}
              <div className="col-md-6">

                <div className="mb-3">
                  <label>Address 1</label>
                  <input
                    className="form-control"
                    name="address_1"
                    value={editForm.address_1}
                    onChange={handleEditInput}
                  />
                </div>

                <div className="mb-3">
                  <label>Address 2</label>
                  <input
                    className="form-control"
                    name="address_2"
                    value={editForm.address_2}
                    onChange={handleEditInput}
                  />
                </div>

                <div className="row mb-3">
                  <div className="col-md-4">
                    <label>City</label>
                    <input
                      className="form-control"
                      name="city"
                      value={editForm.city}
                      onChange={handleEditInput}
                    />
                  </div>

                  <div className="col-md-4">
                    <label>State</label>
                    <input
                      className="form-control"
                      name="state"
                      value={editForm.state}
                      onChange={handleEditInput}
                    />
                  </div>

                  <div className="col-md-4">
                    <label>Zipcode</label>
                    <input
                      className="form-control"
                      name="zipcode"
                      value={editForm.zipcode}
                      onChange={handleEditInput}
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label>Country</label>
                  <Select classNames={{
    control: () => "required-select required-tooltip"
  }}
                    options={countryOptions}
                    value={countryOptions.find(
                      (c) => c.value === editForm.country
                    )}
                    onChange={handleEditCountryChange}
                  />
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <label>Phone Number</label>
                    <input
                      className="form-control"
                      name="phone_number"
                      value={editForm.phone_number}
                      onChange={handleEditInput}
                    />
                  </div>

                  <div className="col-md-6">
                    <label>Supplier</label>
                    <Select classNames={{
    control: () => "required-select required-tooltip"
  }}
                      isMulti
                      options={supplierOptions}
                      value={supplierOptions.filter((s) =>
                        editForm.supplier_ids.includes(s.value)
                      )}
                      onChange={handleEditSupplierChange}
                    />
                  </div>
                </div>

              </div>

            </div>
          </div>

          {/* FOOTER (FIXED LIKE SCREENSHOT) */}
          <div className="modal-footer d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowEdit(false)}
            >
              Close
            </button>

            <button type="submit" className="btn btn-primary">
              Update
            </button>
          </div>

        </form>

      </div>
    </div>
  </div>
)}


    </div>
  );
};

export default ManufacturerPage;
